import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { emptyFilters } from "../../railway-query/config";
import { fetchItineraryRailwayStops } from "../repo/itineraryRailwayViewRepo";
import { dateTabs, fareSelectionId, filterTickets, moveFareSelection, retainAvailableFilters, sortTickets } from "../../railway-query/service";
import type { RailwayFilters, RailwayPurpose, RailwaySeat, RailwaySort, RailwayStop, RailwayTicketsData, RailwayTrain } from "../../railway-query/types";
import { itineraryRailwayFare, matchesRailwayFare, railwayTrain } from "../service/itineraryRailwayViewService";
import type { ItineraryRailwayFare } from "../service/itineraryRailwayViewService";
import type { RailwaySearchResult, TransportOption } from "../types";

export type ItineraryRailwayViewProps = {
  options: TransportOption[]; result: RailwaySearchResult | null; date: string; minDate: string; maxDate: string; purpose: RailwayPurpose;
  fromName: string; toName: string; loading: boolean; error: string; fares: ItineraryRailwayFare[];
  onFaresChange: (fares: ItineraryRailwayFare[]) => void; onSelectDate: (date: string) => void; onQuery: () => void;
};

/** 本地筛选与共享铁路页一致；站点和保存仍由行程抽屉管理。 */
export function useItineraryRailwayView(props: ItineraryRailwayViewProps) {
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState<RailwaySort>({ key: "depart", direction: "asc" });
  const [filtersOpen, setFiltersOpen] = useState(true);
  const [stopTrain, setStopTrain] = useState<RailwayTrain | null>(null);
  const [stops, setStops] = useState<RailwayStop[]>([]);
  const [stopsLoading, setStopsLoading] = useState(false);
  const [stopsError, setStopsError] = useState("");
  const [stopRevision, setStopRevision] = useState(0);
  const data = useMemo<RailwayTicketsData | undefined>(() => props.result ? {
    date: props.result.queryDate, from_station: props.fromName, to_station: props.toName, purpose: props.purpose,
    queried_at: new Date().toISOString(), min_date: props.minDate, max_date: props.maxDate, window_source: "fallback", trains: props.options.map(railwayTrain),
  } : undefined, [props.result, props.options, props.fromName, props.toName, props.purpose, props.minDate, props.maxDate]);
  useEffect(() => { if (data) setFilters((value) => retainAvailableFilters(value, data.trains)); }, [data]);
  // 在新结果可交互前清理旧车次，避免迟到的被动 effect 覆盖刚发生的经停点击。
  useLayoutEffect(() => { setStopTrain(null); }, [props.options]);
  useEffect(() => {
    const controller = new AbortController(); setStops([]); setStopsError(""); setStopsLoading(false);
    if (!stopTrain) return;
    if (!stopTrain.train_no) { setStopsError("当前保存的数据未包含列车经停标识，请重新查询车次后查看。"); return; }
    setStopsLoading(true);
    void fetchItineraryRailwayStops({ date: stopTrain.train_start_date, from_station: stopTrain.origin_code, to_station: stopTrain.destination_code, train_no: stopTrain.train_no }, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setStops(result.stops); })
      .catch((error: unknown) => { if (!controller.signal.aborted) setStopsError(error instanceof Error ? error.message : "经停站查询失败"); })
      .finally(() => { if (!controller.signal.aborted) setStopsLoading(false); });
    return () => controller.abort();
  }, [stopTrain, stopRevision]);
  const displayedFares = props.fares.map((fare) => {
    const option = props.options.find((item) => fare.selection.optionId.startsWith(`${item.optionId}~`) || fare.selection.optionId === item.optionId);
    if (!data || !option || fare.date !== data.date || fare.purpose !== data.purpose || !matchesRailwayFare(fare, [option])) return fare;
    const train = railwayTrain(option);
    const seat = train.seats.find((item) => fare.selection.seatClass === item.name || fare.selection.seatClass?.startsWith(`${item.name} · `));
    if (!seat) return fare;
    const berth = seat.berths.find((item) => fare.selection.seatClass === `${seat.name} · ${item.name}`)?.name ?? null;
    return { ...fare, id: fareSelectionId(data, train, seat, berth) };
  });
  const toggleFare = (train: RailwayTrain, seat: RailwaySeat, berth: string | null = null) => {
    const option = props.options.find((item) => item.optionId === train.id);
    if (!data || !option) return;
    const fare = itineraryRailwayFare(data, option, seat, berth, props.result ?? undefined); if (!fare) return;
    const existing = displayedFares.find((item) => item.id === fare.id || (item.date === fare.date && item.purpose === fare.purpose && item.selection.optionId === fare.selection.optionId));
    props.onFaresChange(existing ? displayedFares.filter((item) => item !== existing) : [fare, ...displayedFares]);
  };
  // 查询尚未返回或失败时也保留当前日期；成功结果仍使用实际查询日。
  const query = { date: data?.date ?? props.date, purpose: props.purpose, from_station: props.fromName, to_station: props.toName };
  return {
    data, query,
    trains: sortTickets(filterTickets(data?.trains ?? [], filters), sort), filters, sort, setSort, filtersOpen, setFilters, setFiltersOpen,
    dates: dateTabs(props.minDate, props.maxDate, query.date), selectDate: props.onSelectDate,
    fromName: props.fromName, toName: props.toName, isLoading: props.loading, error: props.error, retry: props.onQuery,
    dateWindow: undefined, selectedFares: displayedFares, toggleFare,
    removeFare: (id: string) => props.onFaresChange(displayedFares.filter((item) => item.id !== id)),
    moveFare: (id: string, targetId: string) => {
      const order = moveFareSelection(displayedFares, id, targetId);
      props.onFaresChange(order.map((item) => displayedFares.find((fare) => fare.id === item.id)!));
    },
    updateFilters: (patch: Partial<RailwayFilters>) => setFilters((value) => ({ ...value, ...patch })),
    toggleSort: (key: RailwaySort["key"]) => setSort((value) => ({ key, direction: value.key === key && value.direction === "asc" ? "desc" : "asc" })),
    stopTrain, setStopTrain, stops, stopsLoading, stopsError, retryStops: () => setStopRevision((value) => value + 1),
  };
}
